<template>
    <Suspense>
        <template #default>
            <AppLayout />
        </template>

        <template #fallback>
            <div class="global-loader">
                <img class="loader-img" v-bind="data.images.loader" />
            </div>
        </template>
    </Suspense>
</template>

<script lang="ts" setup>
import { inject, nextTick, onBeforeMount, reactive, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import type { Router } from 'vue-router';
import type { VueCookies } from 'vue-cookies';
import { useI18n } from 'vue-i18n';
import { useAppStore, useUserStore, useActionStore, useMenuStore } from '@coreStores/index';
import { useElementStore } from '@stores/element';
import { mx2FaAuth, mxMain, useVersionCheck } from '@app/base-package';
import { get2FaUrl, getMeUrl, getApiSelectOpt, getCookieName, getProfilePage, onRemoveQueryParams } from '@composable/useCommon';
import type { UserTypeKey } from '@composable/useConstant';
import mxLogout from '@mixins/mxLogout';

/* ------ Declaration ------ */
const route = useRoute();
const router = useRouter();
const $cookies = inject<VueCookies>('$cookies');
const appStore = useAppStore();
const userStore = useUserStore();
const actionStore = useActionStore();
const menuStore = useMenuStore();
const elementStore = useElementStore();
const { isLoaded } = storeToRefs(appStore);
const { datas, device } = storeToRefs(userStore);
const { initMainApp, setAppLocale, setAppMenus, setAppMenuPathKey, setAppTheme, setUserData, setUserSettings } = mxMain();
const { init2FaAuth, onPromptOTP } = mx2FaAuth();
const { onLogout } = mxLogout();

appStore.setEnvAttrs(import.meta.env);
appStore.setI18n(useI18n());
$cookies?.config('1d', '/', '', true);

useVersionCheck.appVersionCheck({ app: appStore }, __APP_VERSION__);

/* ------ Data ------ */
const state = reactive({
    isLoaded: false as boolean,
    isRefresh: false as boolean,
    isValidating: false as boolean
});

const data = {
    images: {
        loader: {
            src: '../images/loader/loader.svg',
            alt: 'loader'
        }
    }
};

const cookieOpts = '; path=/; Secure; SameSite=Lax';

/* ------ Before Mount ------ */
onBeforeMount(() => {
    if (!window.Worker) {
        alert('Please update your browser to the latest version.');
    }

    initMainApp(
        { app: appStore, user: userStore, action: actionStore, menu: menuStore },
        {
            cookies: $cookies as VueCookies
        }
    );

    init2FaAuth(
        { app: appStore, user: userStore, action: actionStore },
        {
            cookies: $cookies as VueCookies,
            router: router as Router,
            onLogout,
            get2FaUrl,
            getCookieName
        }
    );
});

/* ------ Methods ------ */
const onDirectRequest = inject('onDirectRequest') as Function;

/**
 * This method retrieves the user details.
 *
 * @param {String} type
 */
const setCurrentMe = async (type: string): Promise<void> => {
    if (!userStore.getLoggedIn()) return;

    let action = getMeUrl(type);

    if (!action) return;

    try {
        await setUserData(action);
        await nextTick();
        setAppMenus();
        getApiSelectOpt(userStore.getType() as UserTypeKey, onDirectRequest);
        onValidatePermission();
    } catch (err) {
        console.error('setCurrentMe failed:', err);
    }
};

/**
 * This method retrieves the user theme.
 */
const setCurrentTheme = (): void => {
    const dmodeCookie = 'dmode' as string;

    if ($cookies?.isKey(dmodeCookie)) {
        userStore.setDarkMode($cookies?.get(dmodeCookie) === 'true');
    }
};

/**
 * This method validate the user login credential & settings.
 */
const onValidateCredential = (): void => {
    const currentType = userStore.getType();

    if (currentType) {
        const cdtCookie = getCookieName(currentType) as string;

        if (currentType != $cookies?.get('type')) {
            document.cookie = `tname=${cdtCookie}${cookieOpts}`;
            document.cookie = `type=${currentType}${cookieOpts}`;
        }

        if ($cookies?.isKey(cdtCookie)) {
            const token = $cookies?.get(cdtCookie);

            if (token) {
                userStore.setLoggedIn(true);
                userStore.setToken(token);
            } else {
                userStore.setLoggedIn(false);
            }
        } else {
            setTimeout(() => {
                router.replace('login');
            }, 1);
        }
    }
};

/**
 * This method validate the user site permission.
 */
const onValidatePermission = (): void => {
    if (state.isValidating) {
        return;
    }

    state.isValidating = true;

    const saKey = appStore.getEnvAttrsByKey('VITE_SUPER_SIGN');

    if (saKey in route.query && route.query[saKey]) {
        onRemoveQueryParams(['super']);
        return;
    }

    const cMenuKey = menuStore.getPermitMenuKey(userStore.getPage());

    if (!cMenuKey) {
        router.push('404');
    }

    const allowedModules = userStore.getInfoByKey('allowed_modules');

    if (!allowedModules || !allowedModules.length) {
        state.isValidating = false;
        return;
    }

    if (!allowedModules.includes(cMenuKey)) {
        router.push('404');
    }

    state.isValidating = false;
};

/* ------ Watches ------ */
/**
 * App Store - isLoaded Watcher.
 */
watch(() => isLoaded, async (newValue, oldValue) => {
    if (newValue) {
        setAppLocale();
        setCurrentTheme();
        elementStore.setDefaultSelectOpt();
        state.isLoaded = true;

        await setAppMenuPathKey(userStore.getType());
        setCurrentMe(userStore.getType());
    }
}, { deep: true });

/**
 * User Store - Data Watcher.
 */
watch(() => ({ ...datas.value }), (newValue, oldValue) => {
    const userType = userStore.getType();
    const cdtCookie = getCookieName(userType) as string;
    const newLoginCookie = getCookieName(userType, { cType: 'new_login' }) as string;

    /* First Login Watcher */
    if (newValue.newLogin && newValue.token) {
        document.cookie = `${cdtCookie}=${encodeURIComponent(newValue.token)}${cookieOpts}`;
        document.cookie = `${newLoginCookie}=1${cookieOpts}`;
    }

    if (newValue.loggedIn) {
        const isNew = Boolean(userStore.getInfoByKey('is_new'));

        if (isNew !== Boolean(newValue.newLogin)) {
            userStore.setNewLogin(isNew);
        }

        if (!isNew) {
            document.cookie = `${newLoginCookie}=0${cookieOpts}`;
        }

        const mustChange = isNew || $cookies?.get(newLoginCookie) === '1';

        if (mustChange && ['afl'].includes(userType)) {
            router.push({ name: 'FirstLoginPassword', params: { type: userType } });
            return;
        }
    }

    /* Logged-In Watcher */
    if (newValue.loggedIn != oldValue.loggedIn) {
        if (newValue.loggedIn) {
            let isDonePush = false;

            if (cdtCookie && !$cookies?.isKey(cdtCookie)) {
                document.cookie = `${cdtCookie}=${encodeURIComponent(newValue.token)}${cookieOpts}`;
                setCurrentMe(userStore.getType());

                if (['adm', 'stg'].includes(userStore.getType())) {
                    const floginCookie = getCookieName(userType, { cType: 'f_login' }) as string;
                    const profilePage = getProfilePage(userStore.getType());

                    if ($cookies?.isKey(floginCookie) && $cookies?.get(floginCookie)) {
                        router.push({ name: 'Home', params: { type: userType } });
                        isDonePush = true;
                    } else {
                        router.push({
                            name: profilePage,
                            query: { isFirst2FA: 1 }
                        });
                        isDonePush = true;
                    }
                } else {
                    router.push({ name: 'Home', params: { type: userType } });
                    isDonePush = true;
                }
            }

            if (state.isRefresh && !isDonePush) {
                state.isRefresh = false;
                router.push({ name: 'Home', params: { type: userType } });
            }

            actionStore.setComponentAction('active-2fa', true);
            // setTestMode({ user: userStore, setting: settingStore });
        } else {
            let apiResponse = actionStore.getResponse();

            if ('message' in apiResponse && apiResponse.message == 'OTP is expired.') {
                onPromptOTP({ isReload: true });
            } else {
                actionStore.setComponentAction('force-logout', true);
            }
        }
    }
});

/**
 * User Store - Device (Language) Watcher.
 */
watch(() => device.value.language, (newValue, oldValue) => {
    if (oldValue && newValue != oldValue) {
        setAppLocale();
    }

    appStore.setCopyright(`Copyright © ${new Date().getFullYear()} <a href="javascript:void(0)">The Affiliate System</a> V${appStore.getEnvAttrsByKey('VITE_APP_VERSION')}. All rights reserved.`);
});

/**
 * User Store - Device (Dark Mode) Watcher.
 */
watch(() => device.value.darkMode, (newValue, oldValue) => {
    document.cookie = `dmode=${newValue}${cookieOpts}`;
    setAppTheme();
});

/**
 * Route URL Watcher.
 */
watch(() => ({ ...route }), (newValue, oldValue) => {
    actionStore.setSelOptRefresh('selRefresh' in newValue.meta);

    if (newValue.path != oldValue.path) {
        setUserSettings(newValue.path.split('/'));
        onValidateCredential();

        if (oldValue.name == undefined && newValue.name == 'Login') {
            state.isRefresh = true;
        }

        if (state.isLoaded) {
            if (oldValue.name == '404' || oldValue.name == '500') {
                setCurrentMe(userStore.getType());
            }

            onValidatePermission();
        }
    }
});

/**
 * Action Store - menu change action Watcher.
 */
watch(() => actionStore.getComponentAction('menu-change'), (newValue) => {
    if (newValue) {
        setCurrentMe(userStore.getType());
        actionStore.setComponentAction('menu-change', false);
    }
}, { deep: true });

/**
 * Action Store - response action Watcher.
 */
watch(() => actionStore.getResponse(), (newValue) => {
    if (newValue) {
        if (newValue.status && newValue.message && newValue.message.toLowerCase() != 'ok') {
            if (actionStore.getSelOptRefresh()) {
                getApiSelectOpt(userStore.getType() as UserTypeKey, onDirectRequest);
            }
        }
    }
}, { deep: true });
</script>